import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  action: () => void;
  description: string;
}

/**
 * Custom hook for managing keyboard navigation throughout the app
 * Provides accessible keyboard shortcuts for common actions
 */
export function useKeyboardNavigation() {
  const navigate = useNavigate();

  const shortcuts: KeyboardShortcut[] = [
    {
      key: "h",
      altKey: true,
      action: () => navigate("/"),
      description: "Go to home page",
    },
    {
      key: "c",
      altKey: true,
      action: () => navigate("/contact"),
      description: "Go to contact page",
    },
    {
      key: "s",
      altKey: true,
      action: () => navigate("/services"),
      description: "Go to services page",
    },
    {
      key: "a",
      altKey: true,
      action: () => navigate("/admissions"),
      description: "Go to admissions page",
    },
    {
      key: "/",
      ctrlKey: true,
      action: () => {
        // Focus search if available
        const searchInput = document.querySelector<HTMLInputElement>('input[type="search"]');
        searchInput?.focus();
      },
      description: "Focus search",
    },
    {
      key: "?",
      shiftKey: true,
      action: () => {
        showKeyboardShortcuts();
      },
      description: "Show keyboard shortcuts",
    },
  ];

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const shortcut = shortcuts.find(
        (s) =>
          s.key.toLowerCase() === event.key.toLowerCase() &&
          !!s.ctrlKey === event.ctrlKey &&
          !!s.shiftKey === event.shiftKey &&
          !!s.altKey === event.altKey
      );

      if (shortcut) {
        event.preventDefault();
        shortcut.action();
      }
    },
    [shortcuts, navigate]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleKeyPress]);

  return { shortcuts };
}

/**
 * Show keyboard shortcuts dialog
 */
function showKeyboardShortcuts() {
  // TODO: Implement shortcuts dialog
  console.log("Keyboard shortcuts:");
  console.log("Alt + H: Home");
  console.log("Alt + C: Contact");
  console.log("Alt + S: Services");
  console.log("Alt + A: Admissions");
  console.log("Ctrl + /: Focus search");
  console.log("Shift + ?: Show shortcuts");
}

/**
 * Hook for managing focus visible styles
 * Adds .keyboard-navigating class to body when Tab is pressed
 */
export function useFocusVisible() {
  useEffect(() => {
    let hadKeyboardEvent = false;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        hadKeyboardEvent = true;
        document.body.classList.add("keyboard-navigating");
      }
    };

    const handleMouseDown = () => {
      hadKeyboardEvent = false;
      document.body.classList.remove("keyboard-navigating");
    };

    const handleFocus = () => {
      if (hadKeyboardEvent) {
        document.body.classList.add("keyboard-navigating");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("focus", handleFocus, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("focus", handleFocus, true);
    };
  }, []);
}

/**
 * Hook for trapping focus within a container (for modals, dialogs)
 */
export function useFocusTrap(isActive: boolean, containerRef: React.RefObject<HTMLElement>) {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Container should handle closing logic
        container.dispatchEvent(new CustomEvent("escape-pressed"));
      }
    };

    document.addEventListener("keydown", handleTabKey);
    document.addEventListener("keydown", handleEscape);

    // Focus first element when trap activates
    firstElement?.focus();

    return () => {
      document.removeEventListener("keydown", handleTabKey);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isActive, containerRef]);
}
